import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'

export interface EmailTemplate {
  id: string
  team_id: string | null
  template_type: 'invitation' | 'reminder' | 'recording_details'
  subject: string
  body: string
  is_default: boolean
  created_at: string
  updated_at: string
}

export interface SendEmailInput {
  guest_id: string
  appearance_id?: string
  template_type: 'invitation' | 'reminder' | 'recording_details'
  variables?: Record<string, string>
}

export interface SendEmailResult {
  success: boolean
  message_id: string
  to: string
  subject: string
}

/**
 * Hook to fetch email templates for the current team
 * Returns team-specific templates or falls back to defaults
 */
export function useEmailTemplates() {
  const supabase = createClient()
  const { membership } = useAuth()

  return useQuery({
    queryKey: ['email-templates', membership?.team_id],
    queryFn: async (): Promise<EmailTemplate[]> => {
      // Fetch all templates (team-specific and defaults)
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .or(`team_id.eq.${membership?.team_id},team_id.is.null`)
        .order('template_type')
        .order('team_id', { ascending: false, nullsFirst: false })

      if (error) throw error

      // Deduplicate: prefer team-specific over defaults
      const templateMap = new Map<string, EmailTemplate>()
      for (const template of (data as EmailTemplate[]) || []) {
        if (!templateMap.has(template.template_type)) {
          templateMap.set(template.template_type, template)
        }
      }

      return Array.from(templateMap.values())
    },
    enabled: !!membership?.team_id,
  })
}

/**
 * Hook to get a specific email template by type
 */
export function useEmailTemplate(templateType: 'invitation' | 'reminder' | 'recording_details') {
  const { data: templates, ...rest } = useEmailTemplates()

  const template = templates?.find((t) => t.template_type === templateType)

  return {
    data: template,
    ...rest,
  }
}

/**
 * Hook to send an email via the send-email edge function
 */
export function useSendEmail() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SendEmailInput): Promise<SendEmailResult> => {
      // Get access token for authorization
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token
      if (!accessToken) {
        throw new Error('No access token available. Please log in again.')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(input),
        }
      )

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send email')
      }
      return data as SendEmailResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications'] })
    },
  })
}

/**
 * Input for composing a custom email
 */
export interface ComposeEmailInput {
  guest_id: string
  appearance_id?: string
  subject: string
  body: string
}

/**
 * Hook to send a custom composed email via the compose-email edge function
 */
export function useSendCustomEmail() {
  const supabase = createClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: ComposeEmailInput): Promise<SendEmailResult> => {
      // Get access token for authorization
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token
      if (!accessToken) {
        throw new Error('No access token available. Please log in again.')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/compose-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify(input),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page and try again.')
        }
        throw new Error(data.error || 'Failed to send email')
      }

      // Check if the response contains an error (edge function returned error JSON)
      if (data?.error) {
        throw new Error(data.error)
      }

      return data as SendEmailResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications'] })
    },
  })
}

/**
 * Hook to preview an email template with variables substituted
 */
export function usePreviewEmail() {
  return useMutation({
    mutationFn: async ({
      template,
      variables,
    }: {
      template: EmailTemplate
      variables: Record<string, string>
    }): Promise<{ subject: string; body: string }> => {
      // Simple template variable substitution (client-side preview)
      const processTemplate = (text: string): string => {
        let result = text

        // Process {{#if variable}}content{{/if}} blocks
        const ifRegex = /\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g
        result = result.replace(ifRegex, (_match, varName, content) => {
          const value = variables[varName]
          return value && value.trim() ? content : ''
        })

        // Process simple {{variable}} substitutions
        const varRegex = /\{\{(\w+)\}\}/g
        result = result.replace(varRegex, (_match, varName) => {
          return variables[varName] || `[${varName}]`
        })

        return result
      }

      return {
        subject: processTemplate(template.subject),
        body: processTemplate(template.body),
      }
    },
  })
}
